import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslateText } from "../api/hooks";
import { detectMessageLanguage } from "../utils/detect-message-language";
import { TRANSLATE_TEXT_MAX_LENGTH } from "../types";
import type { MessageDirection, TranslationLanguage } from "../types";

const TRANSLATABLE_SOURCE: Record<MessageDirection, TranslationLanguage> = {
  inbound: "es",
  outbound: "en"
};

const targetFor = (
  direction: MessageDirection,
  detected: TranslationLanguage | null
): TranslationLanguage | null => {
  if (detected !== TRANSLATABLE_SOURCE[direction]) return null;
  return detected === "es" ? "en" : "es";
};

interface UseMessageTranslationArgs {
  text: string;
  direction: MessageDirection;
  enabled: boolean;
  subject?: string;
}

export const useMessageTranslation = ({
  text: originalText,
  direction,
  enabled,
  subject: subjectText
}: UseMessageTranslationArgs) => {
  const { mutate, isPending: isTranslating } = useTranslateText();
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [translatedSubject, setTranslatedSubject] = useState<string | null>(
    null
  );
  const [showingTranslation, setShowingTranslation] = useState(false);
  const [targetLanguage, setTargetLanguage] =
    useState<TranslationLanguage | null>(null);
  // Guards against a stale onSuccess if this hook instance gets reused for
  // a different message (list virtualization).
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    setTranslatedText(null);
    setTranslatedSubject(null);
    setShowingTranslation(false);
  }, [originalText, subjectText]);

  useEffect(() => {
    if (!enabled) {
      setTargetLanguage(null);
      return;
    }
    let cancelled = false;
    setTargetLanguage(null);
    detectMessageLanguage(originalText)
      .then((detected) => {
        if (!cancelled) setTargetLanguage(targetFor(direction, detected));
      })
      .catch((error: unknown) => {
        // Detector failed to load/run — hide the button instead of hanging.
        if (!cancelled) setTargetLanguage(null);
        // eslint-disable-next-line no-console
        console.error("Message language detection failed:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [originalText, enabled, direction]);

  const translate = useCallback(() => {
    if (translatedText !== null) {
      setShowingTranslation(true);
      return;
    }
    const trimmedLength = originalText.trim().length;
    if (!trimmedLength || !targetLanguage) return;
    if (trimmedLength > TRANSLATE_TEXT_MAX_LENGTH) {
      toast.error(
        `Message is too long to translate (max ${TRANSLATE_TEXT_MAX_LENGTH.toLocaleString()} characters).`
      );
      return;
    }
    const generation = generationRef.current;

    mutate(
      {
        text: originalText,
        targetLanguage,
        ...(subjectText?.trim() && { subject: subjectText })
      },
      {
        onSuccess: ({ translatedText: result, translatedSubject: subject }) => {
          if (generation !== generationRef.current) return;
          setTranslatedText(result);
          if (typeof subject === "string") setTranslatedSubject(subject);
          setShowingTranslation(true);
        },
        onError: () => {
          if (generation !== generationRef.current) return;
          toast.error("We couldn't translate your message. Try again.");
        }
      }
    );
  }, [originalText, subjectText, targetLanguage, translatedText, mutate]);

  const showOriginal = useCallback(() => setShowingTranslation(false), []);

  return {
    translate,
    showOriginal,
    showingTranslation,
    translatedText,
    translatedSubject,
    targetLanguage,
    isTranslating
  };
};

export type MessageTranslation = ReturnType<typeof useMessageTranslation>;
